import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { PositionResource } from '../../resources';
import { AccountCategory, collateralSummaryAtom } from '../../state';
import { CollateralSummaryAmount, CollateralSummaryItem, SelectedAccountOrClient } from '../../types';
import {
  groupByAndMergeNumericValues,
  groupByAndMergeNumericValuesMultipleKeys,
  isArrayEqual,
  isDefined,
  Loadable,
  LoadableState,
} from '../../utils';
import { GetResourceHeaderForClient } from '../useResource';

type AdditionalPropsForAllAccounts = Pick<
  CollateralSummaryItem,
  'assets' | 'debtorAndGuarantors' | 'loans' | 'positions' | 'nonMargin' | 'accountNumber' | 'difference'
>;

type AllAccountsCollateralSummaryItem = CollateralSummaryAmount & AdditionalPropsForAllAccounts;

export type CollateralSummaryProps = {
  positionResource: PositionResource;
  getResourceHeaderForClient: GetResourceHeaderForClient;
  selectedAccountOrClient: SelectedAccountOrClient | undefined;
};

export const useCollateralSummaryCommon = ({
  positionResource,
  getResourceHeaderForClient,
  selectedAccountOrClient,
}: CollateralSummaryProps) => {
  const [collateralSummary, setCollateralSummary] = useAtom(collateralSummaryAtom);

  const loadCollateralSummary = useCallback(
    async (force: boolean = false) => {
      const resourceHeaders = getResourceHeaderForClient();

      if (collateralSummary.isLoading) {
        return;
      }

      const isUnitialized =
        collateralSummary.isLoaded === false &&
        collateralSummary.isLoading === false &&
        collateralSummary.hasError === false;

      if (
        force ||
        isUnitialized ||
        (collateralSummary.isLoaded &&
          isArrayEqual(collateralSummary.lastAccountList, resourceHeaders.accountId) === false)
      ) {
        setCollateralSummary(Loadable.loading);

        try {
          const response = await positionResource.getCollateralDetails(resourceHeaders);

          setCollateralSummary(
            Loadable.loaded({
              items: response.data.collateralSummaryItems.map((item: CollateralSummaryItem) => ({
                ...item,
                positions: item.positions.map(position => ({
                  ...position,
                  id: `${position.instrumentName}-${position.quantity > 0 ? 'long' : 'short'}`,
                })),
              })),
              lastAccountList: resourceHeaders.accountId,
              collateralSummaryAmountsByClientId: response.data.collateralSummaryAmountsByClientId[0],
            }),
          );
        } catch (error) {
          setCollateralSummary(Loadable.failed);
        }
      }
    },
    [setCollateralSummary, positionResource, getResourceHeaderForClient, collateralSummary],
  );

  const investmentLoan: LoadableState<number> = useMemo(() => {
    if (collateralSummary.isLoaded) {
      const value =
        collateralSummary.collateralSummaryAmountsByClientId.collateralValue -
        collateralSummary.collateralSummaryAmountsByClientId.marginRequirement;
      // investment loan is mined to 0 and maxed to 50,000,000
      return Loadable.loaded({
        value: Math.min(Math.max(value, 0), 50000000),
      });
    }

    return collateralSummary as LoadableState<number>;
  }, [collateralSummary]);

  const isSelectedAccountNyesz = useMemo(
    () => selectedAccountOrClient?.account?.accountCategory === AccountCategory.Nyesz,
    [selectedAccountOrClient?.account?.accountCategory],
  );

  // In case of 'all accounts' (or client) selected we have to merge each account together
  const mergedClientSummary: AllAccountsCollateralSummaryItem | undefined = useMemo(() => {
    if (!collateralSummary.isLoaded) {
      return undefined;
    }

    const mergedAdditionalProps = collateralSummary.items.reduce(
      (acc, next) => ({
        ...acc,
        assets: [...acc.assets, ...next.assets],
        debtorAndGuarantors: [...acc.debtorAndGuarantors, ...next.debtorAndGuarantors],
        loans: [...acc.loans, ...next.loans],
        positions: [...acc.positions, ...next.positions],
        nonMargin: acc.nonMargin + next.nonMargin,
      }),
      {
        ...collateralSummary.collateralSummaryAmountsByClientId,
        assets: [],
        debtorAndGuarantors: [],
        loans: [],
        positions: [],
        nonMargin: 0,
        accountNumber: '',
      } as AdditionalPropsForAllAccounts,
    );

    return {
      ...collateralSummary.collateralSummaryAmountsByClientId,
      ...mergedAdditionalProps,
    };
  }, [collateralSummary]);

  // Handle identical data by grouping and then merging the numeric values represented in the 'included' fields.
  const activeCollateralSummary = useMemo((): CollateralSummaryItem | undefined => {
    if (!selectedAccountOrClient || !collateralSummary.isLoaded) {
      return undefined;
    }

    const selectedCollateralSummary = selectedAccountOrClient.client
      ? mergedClientSummary
      : collateralSummary.items.find(item => item.accountNumber === selectedAccountOrClient.account?.accountNumber);

    if (!selectedCollateralSummary) {
      return undefined;
    }

    return {
      ...selectedCollateralSummary,
      assets: groupByAndMergeNumericValues(selectedCollateralSummary.assets, 'name', {
        onlyFirstValueFields: ['discount', 'price'],
        include: ['collateralValue', 'quantity', 'marketValue', 'nonMargin'],
      }),
      positions: groupByAndMergeNumericValuesMultipleKeys(
        selectedCollateralSummary.positions,
        ['instrumentName', 'id'],
        {
          include: ['nonMargin', 'marginRequirement', 'quantity', 'profitLossHuf'],
        },
      ),
      loans: selectedCollateralSummary.loans.map((l, ind) => ({ ...l, id: `${ind}_${l.name}` })), // If no grouping is required then create a unique id for the key prop
      debtorAndGuarantors: groupByAndMergeNumericValuesMultipleKeys(
        selectedCollateralSummary.debtorAndGuarantors,
        ['clientId', 'partyType'],
        { onlyFirstValueFields: ['collateral', 'guarantorAsset'] },
      ),
    };
  }, [selectedAccountOrClient, collateralSummary, mergedClientSummary]);

  const isAccountEmpty = useMemo(
    () =>
      isDefined(activeCollateralSummary) &&
      activeCollateralSummary.totalCollateralValue === 0 &&
      activeCollateralSummary.positions.length === 0 &&
      activeCollateralSummary.loans.length === 0 &&
      activeCollateralSummary.assets.length === 0 &&
      activeCollateralSummary.debtorAndGuarantors.length === 0,
    [activeCollateralSummary],
  );

  return useMemo(
    () => ({
      collateralSummary,
      loadCollateralSummary,
      investmentLoan,
      isAccountEmpty,
      isSelectedAccountNyesz,
      activeCollateralSummary,
      isLoading: collateralSummary.isLoading,
    }),
    [
      collateralSummary,
      loadCollateralSummary,
      investmentLoan,
      isAccountEmpty,
      isSelectedAccountNyesz,
      activeCollateralSummary,
    ],
  );
};
